import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { calculateLifestyleCreep } from '../../lib/calculations/lifestyleCreep'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import FeatureGate from '../FeatureGate'
import { trackExportReport } from '../../lib/analytics'
import { addChartToPDF } from '../../lib/pdfChartCapture'

function InputField({ label, value, onChange, prefix, suffix, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:border-primary transition ${prefix ? 'pl-7' : 'pl-3'} ${suffix ? 'pr-12' : 'pr-3'}`}
          min={0}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-faint text-xs">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-faint mt-1">{hint}</p>}
    </div>
  )
}

function ResultCard({ label, value, color, highlight, sub }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
      {sub && <p className="text-xs text-faint mt-0.5">{sub}</p>}
    </GlassCard>
  )
}

export default function LifestyleCreep() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [startingSalary, setStartingSalary] = useState(50000)
  const [currentSalary, setCurrentSalary] = useState(85000)
  const [yearsWorking, setYearsWorking] = useState(7)
  const [startingMonthlyExpenses, setStartingMonthlyExpenses] = useState(2000)
  const [currentMonthlyExpenses, setCurrentMonthlyExpenses] = useState(4000)
  const [investmentReturnRate, setInvestmentReturnRate] = useState(8)

  const result = useMemo(() => calculateLifestyleCreep({
    startingSalary,
    currentSalary,
    yearsWorking,
    startingMonthlyExpenses,
    currentMonthlyExpenses,
    investmentReturnRate,
  }), [startingSalary, currentSalary, yearsWorking, startingMonthlyExpenses, currentMonthlyExpenses, investmentReturnRate])

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 14

    // --- Helper: check if we need a new page ---
    const ensureSpace = (needed, currentY) => {
      if (currentY + needed > pageHeight - margin) {
        doc.addPage()
        return margin + 4
      }
      return currentY
    }

    // --- Title & date ---
    doc.setFillColor(255, 255, 255)
    doc.rect(0, 0, pageWidth, pageHeight, 'F')

    doc.setFontSize(20)
    doc.setTextColor(30, 30, 30)
    doc.text('Lifestyle Creep Analysis', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128, 128, 128)
    doc.text(`Generated by Liquid Finance  \u2022  ${new Date().toLocaleDateString()}`, margin, 30)

    let y = 38

    // --- Section 1: Input Parameters ---
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Input Parameters', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Parameter', 'Value']],
      body: [
        ['Starting Salary', `${formatCurrency(startingSalary)}/year`],
        ['Current Salary', `${formatCurrency(currentSalary)}/year`],
        ['Years of Raises', `${yearsWorking} years`],
        ['Starting Monthly Expenses', `${formatCurrency(startingMonthlyExpenses)}/month`],
        ['Current Monthly Expenses', `${formatCurrency(currentMonthlyExpenses)}/month`],
        ['Investment Return Rate', `${investmentReturnRate}%`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 2: Key Results (mirrors the 4 result cards) ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Key Results', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Metric', 'Value', 'Note']],
      body: [
        ['Wealth Lost to Creep', formatCurrency(result.lostWealth), 'If spending stayed flat'],
        ['Current Savings Rate', `${result.currentSavingsRate}%`, `Was ${result.startingSavingsRate}%`],
        ['Potential Savings Rate', `${result.potentialSavingsRate}%`, 'With starting expenses'],
        ['Creep Rate', `${result.creepRate}%`, 'Spending increase'],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      columnStyles: { 2: { textColor: [120, 120, 120], fontStyle: 'italic' } },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 3: Detailed Breakdown ---
    y = ensureSpace(40, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Income & Spending Breakdown', margin, y)
    y += 4

    autoTable(doc, {
      startY: y,
      head: [['Detail', 'Value']],
      body: [
        ['Total Raises Received', formatCurrency(result.totalRaises)],
        ['Spending Increase', `${formatCurrency(result.spendingIncrease)}/year`],
        ['Actual Wealth Built', formatCurrency(result.actualWealth)],
        ['Disciplined Wealth (if flat spending)', formatCurrency(result.disciplinedWealth)],
        ['Wealth Difference (Lost to Creep)', formatCurrency(result.lostWealth)],
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 9 },
      bodyStyles: { fontSize: 9, textColor: [40, 40, 40] },
      didParseCell: (data) => {
        if (data.section === 'body' && data.row.index === 4) {
          data.cell.styles.fontStyle = 'bold'
          data.cell.styles.fillColor = [254, 226, 226]
        }
      },
      margin: { left: margin, right: margin },
    })
    y = doc.lastAutoTable.finalY + 10

    // --- Section 4: Insight Banner ---
    y = ensureSpace(30, y)
    doc.setFontSize(13)
    doc.setTextColor(50, 50, 50)
    doc.text('Insight', margin, y)
    y += 6
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)

    const insightTitle = `You earned ${formatCurrency(result.totalRaises)} in raises over ${yearsWorking} years.`
    doc.setFontSize(10)
    doc.setTextColor(40, 40, 40)
    doc.text(insightTitle, margin, y)
    y += 6

    const insightBody = `But your spending increased by ${formatCurrency(result.spendingIncrease)}/year. If you had kept spending at your starting level and invested the difference at ${investmentReturnRate}%, you'd have ${formatCurrency(result.disciplinedWealth)} instead of ${formatCurrency(result.actualWealth)} \u2014 a difference of ${formatCurrency(result.lostWealth)}.`
    doc.setFontSize(9)
    doc.setTextColor(80, 80, 80)
    const insightLines = doc.splitTextToSize(insightBody, pageWidth - margin * 2)
    doc.text(insightLines, margin, y)
    y += insightLines.length * 4.5 + 8

    // --- Section 5: Chart (Actual vs Disciplined Path) ---
    await addChartToPDF(doc, chartRef, { title: 'Wealth Growth: Actual vs Disciplined Path' })

    // --- Section 6: Year-by-Year Table ---
    if (result.timeline.length > 0) {
      doc.addPage()
      let tableY = margin + 4

      doc.setFontSize(13)
      doc.setTextColor(50, 50, 50)
      doc.text('Year-by-Year Breakdown', margin, tableY)
      tableY += 4

      autoTable(doc, {
        startY: tableY,
        head: [['Year', 'Actual Savings', 'Disciplined Savings', 'Actual Wealth', 'Disciplined Wealth', 'Difference']],
        body: result.timeline.map(row => [
          `Year ${row.year}`,
          formatCurrency(row.actualSavings),
          formatCurrency(row.disciplinedSavings),
          formatCurrency(row.actualWealth),
          formatCurrency(row.disciplinedWealth),
          formatCurrency(row.disciplinedWealth - row.actualWealth),
        ]),
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 8 },
        bodyStyles: { fontSize: 8, textColor: [40, 40, 40] },
        columnStyles: {
          4: { textColor: [22, 163, 74] },
          5: { textColor: [220, 38, 38], fontStyle: 'italic' },
        },
        margin: { left: margin, right: margin },
      })
    }

    doc.save(`lifestyle-creep-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Income & Spending History</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField label="Starting Salary" value={startingSalary} onChange={setStartingSalary} prefix="$" suffix="/year" />
          <InputField label="Current Salary" value={currentSalary} onChange={setCurrentSalary} prefix="$" suffix="/year" />
          <InputField label="Years of Raises" value={yearsWorking} onChange={setYearsWorking} suffix="years" />
          <InputField label="Starting Monthly Expenses" value={startingMonthlyExpenses} onChange={setStartingMonthlyExpenses} prefix="$" suffix="/month" />
          <InputField label="Current Monthly Expenses" value={currentMonthlyExpenses} onChange={setCurrentMonthlyExpenses} prefix="$" suffix="/month" />
          <InputField label="Investment Return Rate" value={investmentReturnRate} onChange={setInvestmentReturnRate} suffix="%" hint="Assumed growth on invested savings" />
        </div>
      </GlassCard>

      {/* Result Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="Wealth Lost to Creep"
          value={formatCurrency(result.lostWealth)}
          color={colors.negative}
          highlight
          sub="If spending stayed flat"
        />
        <ResultCard
          label="Current Savings Rate"
          value={`${result.currentSavingsRate}%`}
          color={result.currentSavingsRate >= 20 ? colors.positive : colors.warning}
          sub={`Was ${result.startingSavingsRate}%`}
        />
        <ResultCard
          label="Potential Savings Rate"
          value={`${result.potentialSavingsRate}%`}
          color={colors.positive}
          sub="With starting expenses"
        />
        <ResultCard
          label="Creep Rate"
          value={`${result.creepRate}%`}
          color={result.creepRate > 50 ? colors.negative : colors.warning}
          sub="Spending increase"
        />
      </div>

      {/* Insight Banner */}
      <GlassCard className="!p-4" style={{ borderLeft: `3px solid ${colors.negative}` }}>
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${colors.negative}22`, color: colors.negative }}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-medium text-heading mb-1">
              You earned {formatCurrency(result.totalRaises)} in raises over {yearsWorking} years
            </p>
            <p className="text-xs text-muted leading-relaxed">
              But your spending increased by {formatCurrency(result.spendingIncrease)}/year.
              If you had kept spending at your starting level and invested the difference at {investmentReturnRate}%,
              you'd have <strong className="text-heading">{formatCurrency(result.disciplinedWealth)}</strong> instead
              of <strong className="text-heading">{formatCurrency(result.actualWealth)}</strong> —
              a difference of <strong style={{ color: colors.negative }}>{formatCurrency(result.lostWealth)}</strong>.
            </p>
          </div>
        </div>
      </GlassCard>

      {/* Chart */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Wealth Growth: Actual vs Disciplined Path</h3>
        <div ref={chartRef} className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={result.timeline}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis dataKey="year" tickFormatter={v => `Yr ${v}`} stroke={colors.chartTick} tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} stroke={colors.chartTick} tick={{ fontSize: 11 }} />
              <Tooltip
                content={({ payload, label }) => {
                  if (!payload?.[0]) return null
                  return (
                    <div className="glass px-3 py-2 text-xs rounded-lg">
                      <p className="text-heading font-medium mb-1">Year {label}</p>
                      {payload.map((p, i) => (
                        <p key={i} style={{ color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
                      ))}
                    </div>
                  )
                }}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="disciplinedWealth"
                name="Disciplined Path"
                stroke={colors.positive}
                fill={colors.positive}
                fillOpacity={0.15}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="actualWealth"
                name="Actual Path"
                stroke={colors.warning}
                fill={colors.warning}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Year-by-year table */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Year-by-Year Breakdown</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-subtle">
                <th className="text-left py-2 text-muted font-medium">Year</th>
                <th className="text-right py-2 text-muted font-medium">Actual Savings</th>
                <th className="text-right py-2 text-muted font-medium">Disciplined Savings</th>
                <th className="text-right py-2 text-muted font-medium">Actual Wealth</th>
                <th className="text-right py-2 text-muted font-medium">Disciplined Wealth</th>
              </tr>
            </thead>
            <tbody>
              {result.timeline.map((row) => (
                <tr key={row.year} className="border-b border-subtle">
                  <td className="py-2 text-body">Year {row.year}</td>
                  <td className="py-2 text-right text-heading">{formatCurrency(row.actualSavings)}</td>
                  <td className="py-2 text-right text-positive">{formatCurrency(row.disciplinedSavings)}</td>
                  <td className="py-2 text-right text-heading">{formatCurrency(row.actualWealth)}</td>
                  <td className="py-2 text-right text-positive">{formatCurrency(row.disciplinedWealth)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassCard>
    </div>
  )
}
